import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { UserProfile, OrderDetails, UserContextProviderType } from '../types';
import api from '../services/api';

const UserContext = createContext<UserContextProviderType | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [orders, setOrders] = useState<OrderDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        if (!authUser) return;

        // We already have the backend user from AuthContext, but let's ensure we have the latest data
        // The backendUser from AuthContext is type User, we need to map it to UserProfile

        // Fetch latest user data to get preferences
        const response = await api.get<{ user: any }>('/auth/me');
        const userData = response.user;

        const preferences = userData.preferences || {};

        const profile: UserProfile = {
          id: userData.firebase_uid,
          email: userData.email,
          displayName: userData.name,
          photoURL: authUser.photoURL,
          phoneNumber: preferences.phone_number || '',
          address: {
            street: preferences.address || '',
            city: '', // Not stored separately yet
            state: '', // Not stored separately yet
            zipCode: '', // Not stored separately yet
            country: '', // Not stored separately yet
          },
          preferences: {
            emailNotifications: preferences.emailNotifications ?? true,
            promotions: preferences.promotions ?? true,
          },
          createdAt: userData.created_at,
        };

        setUserProfile(profile);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOrders = async () => {
      try {
        const data = await api.get<OrderDetails[]>('/orders/my');

        // Map backend order to frontend OrderDetails
        // Backend returns items with product relationship
        // Frontend expects items with specific structure
        const mappedOrders = data.map((order: any) => ({
          ...order,
          total_amount: order.total, // Map backend 'total' to frontend 'total_amount'
          items: order.items.map((item: any) => ({
            productId: item.product.id.toString(),
            name: item.product.name,
            quantity: item.quantity,
            price: item.price, // Map backend 'price' correctly
            image: item.product.image_url,
          })),
          shippingAddress: {
            street: order.shipping_address,
            city: '',
            state: '',
            zipCode: '',
          }
        }));

        setOrders(mappedOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    };

    if (authUser) {
      fetchUserProfile();
      fetchOrders();
    } else {
      setUserProfile(null);
      setOrders([]);
    }
  }, [authUser]);

  const updateUserProfile = async (updates: Partial<UserProfile>): Promise<{ success: boolean; error?: any }> => {
    try {
      // Map frontend updates to backend structure
      const backendUpdates: any = {};

      if (updates.displayName) backendUpdates.name = updates.displayName;
      if (updates.phoneNumber) backendUpdates.phone_number = updates.phoneNumber;
      if (updates.address?.street) backendUpdates.address = updates.address.street;

      await api.put('/users/profile', backendUpdates);

      setUserProfile((prev) => (prev ? { ...prev, ...updates } : null));
      return { success: true };
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { success: false, error };
    }
  };

  const updatePreferences = async (preferences: Partial<UserProfile['preferences']>): Promise<{ success: boolean; error?: any }> => {
    try {
      await api.put('/users/preferences', preferences);

      setUserProfile((prev) =>
        prev ? {
          ...prev,
          preferences: { ...prev.preferences, ...preferences },
        } : null
      );
      return { success: true };
    } catch (error) {
      console.error('Error updating preferences:', error);
      return { success: false, error };
    }
  };

  const getOrderById = (orderId: string): OrderDetails | undefined => {
    return orders.find((order) => order.id === parseInt(orderId));
  };

  const value: UserContextProviderType = {
    userProfile,
    orders,
    loading,
    updateUserProfile,
    updatePreferences,
    getOrderById,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProviderType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
